import {
  DomainSourceSchema,
  type DomainConfig,
} from '@terra/shared-schemas-orchestrator';
import { resolveDomainTestingScope } from './domain-config-sync.util';

const INTERFACE_DOMAIN_SOURCE = DomainSourceSchema.enum.PROXY;

const createInterfaceDomainConfig = ({
  domain,
  createdAt,
  enabledAt,
}: {
  domain: string;
  createdAt: Date;
  enabledAt: Date;
}): DomainConfig => ({
  domain,
  source: INTERFACE_DOMAIN_SOURCE,
  isInFunctionalityScope: true,
  isInTestingScope: true,
  isThirdParty: false,
  hasWaf: null,
  createdAt,
  enabledAt,
});

export function extractUrlHostname(url: string): string | null {
  const match = url.trim().match(/^https?:\/\/([^/?#]+)/i);
  if (!match?.[1]) {
    return null;
  }

  const [hostname] = match[1].split(':');
  return hostname ? hostname.toLowerCase() : null;
}

export function extractInterfaceDomains({
  primaryUrl,
  interfaces,
}: {
  primaryUrl: string;
  interfaces: { url: string }[];
}): string[] {
  const primaryHostname = extractUrlHostname(primaryUrl);
  const uniqueHosts = new Set<string>();

  for (const iface of interfaces) {
    const hostname = extractUrlHostname(iface.url);
    if (hostname && hostname !== primaryHostname) {
      uniqueHosts.add(hostname);
    }
  }

  return [...uniqueHosts];
}

export function buildEnabledInterfaceDomainConfigs(
  hostnames: string[],
): DomainConfig[] {
  const now = new Date();

  return hostnames.map((domain) =>
    createInterfaceDomainConfig({ domain, createdAt: now, enabledAt: now }),
  );
}

export function mergeInterfaceDomains({
  existingDomains,
  interfaceHostnames,
}: {
  existingDomains: DomainConfig[];
  interfaceHostnames: string[];
}): DomainConfig[] {
  const merged = existingDomains.map((domain) => ({ ...domain }));
  const now = new Date();

  for (const hostname of interfaceHostnames) {
    const existing = merged.find((domain) => domain.domain === hostname);

    if (existing) {
      if (!resolveDomainTestingScope(existing)) {
        Object.assign(existing, {
          isInFunctionalityScope: true,
          isInTestingScope: true,
          isThirdParty: false,
          enabledAt: now,
        });
      }
      continue;
    }

    merged.push(
      createInterfaceDomainConfig({
        domain: hostname,
        createdAt: now,
        enabledAt: now,
      }),
    );
  }

  return merged;
}
